import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ingestSoap, ingestInvoice } from "@/api/ingest";
import { runAudit } from "@/api/audit";
import { AuditReportView } from "@/components/AuditReportView";
import { useToasts } from "@/hooks/useToast";
import { useAuthStore } from "@/store/auth";
import { parseInvoiceLines } from "@/utils/parseInvoice";
import type { ApiError } from "@/types";

const DEMO_SOAP = `Patient: Buddy, 4yr M/N Golden Retriever. Presented for annual wellness.

Physical exam unremarkable. Weight 32kg.

Procedures performed:
- Annual wellness exam completed
- Administered DHPP combo vaccine
- Administered Rabies 1yr vaccine
- Administered Bordetella intranasal
- SQ fluids 250ml LRS (mild dehydration noted)
- In-house CBC + Chemistry panel performed
- Flea/tick preventative (Nexgard) applied
- Ear cytology — mild yeast overgrowth
- Nail trim performed
- Prescribed Meloxicam 1.5mg PO q24h x7d (OA left elbow)
- 4Dx heartworm test — negative

Follow-up in 12 months.`;

const DEMO_INVOICE = `Annual wellness exam
DHPP vaccine
Rabies vaccine
Flea/tick preventative (Nexgard)
CBC bloodwork`;

const schema = z.object({
  patient_name: z.string().min(1, "Patient name required").max(256),
  vet_name: z.string().min(1, "Vet name required").max(256),
  soap_notes: z.string().min(10, "SOAP notes must be at least 10 characters").max(50_000),
  invoice_raw: z.string().min(1, "At least one invoice line required"),
});

type FormValues = z.infer<typeof schema>;

type Step = "input" | "running" | "results";

export function AuditPage() {
  const clinicId = useAuthStore((s) => s.clinicId);
  const push = useToasts((s) => s.push);

  const [step, setStep] = useState<Step>("input");
  const [statusText, setStatusText] = useState("");
  const [appointmentId, setAppointmentId] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      patient_name: "",
      vet_name: "",
      soap_notes: "",
      invoice_raw: "",
    },
  });

  const loadDemo = () => {
    setValue("patient_name", "Buddy");
    setValue("vet_name", "Dr. Smith");
    setValue("soap_notes", DEMO_SOAP);
    setValue("invoice_raw", DEMO_INVOICE);
  };

  const reset = () => {
    setAppointmentId(null);
    setStep("input");
    setStatusText("");
  };

  // Sequential workflow: ingest SOAP → ingest invoice → run audit.
  // Handled step-by-step (not as a single mutation) so we can surface which
  // step failed and not re-run successful steps on retry.
  const onSubmit = async (values: FormValues) => {
    if (!clinicId) {
      push("No clinic ID set. Set VITE_DEV_CLINIC_ID or log in.", "error");
      return;
    }

    setStep("running");

    try {
      setStatusText("Extracting clinical events from SOAP notes…");
      const soap = await ingestSoap({
        clinic_id: clinicId,
        patient_name: values.patient_name,
        vet_name: values.vet_name,
        soap_notes: values.soap_notes,
      });

      setStatusText(`Ingesting ${parseInvoiceLines(values.invoice_raw).length} invoice items…`);
      await ingestInvoice({
        clinic_id: clinicId,
        appointment_id: soap.appointment_id,
        items: parseInvoiceLines(values.invoice_raw),
      });

      setStatusText("Reconciling against invoice (this can take a few seconds)…");
      await runAudit(soap.appointment_id);

      setAppointmentId(soap.appointment_id);
      setStep("results");
      push(`Audit complete — ${soap.clinical_events_count} events extracted`, "success");
    } catch (err) {
      const msg = (err as unknown as ApiError).message ?? "Audit failed";
      push(msg, "error");
      setStep("input");
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1>Run billing audit</h1>
        <p className="page-sub">
          Paste SOAP notes and the invoice, and the engine will flag anything that was done but not
          billed.
        </p>
      </div>

      {step === "input" && (
        <form className="card" onSubmit={handleSubmit(onSubmit)}>
          <div className="row" style={{ marginBottom: 16 }}>
            <button type="button" className="btn btn-sm" onClick={loadDemo}>
              Load demo data
            </button>
          </div>

          <div className="row" style={{ gap: 16 }}>
            <div className="field grow">
              <label htmlFor="patient_name">Patient name</label>
              <input id="patient_name" type="text" {...register("patient_name")} />
              {errors.patient_name && (
                <span className="field-error">{errors.patient_name.message}</span>
              )}
            </div>
            <div className="field grow">
              <label htmlFor="vet_name">Vet name</label>
              <input id="vet_name" type="text" {...register("vet_name")} />
              {errors.vet_name && <span className="field-error">{errors.vet_name.message}</span>}
            </div>
          </div>

          <div className="field">
            <label htmlFor="soap_notes">SOAP notes</label>
            <textarea
              id="soap_notes"
              {...register("soap_notes")}
              placeholder="Paste SOAP notes here…"
            />
            {errors.soap_notes && <span className="field-error">{errors.soap_notes.message}</span>}
          </div>

          <div className="field">
            <label htmlFor="invoice_raw">
              Invoice items (one per line, optionally{" "}
              <span className="mono">description 25.00</span>)
            </label>
            <textarea
              id="invoice_raw"
              style={{ minHeight: 120 }}
              {...register("invoice_raw")}
              placeholder="DHPP vaccine 25.00&#10;Rabies vaccine 20.00&#10;Annual wellness exam 75.00"
            />
            {errors.invoice_raw && (
              <span className="field-error">{errors.invoice_raw.message}</span>
            )}
          </div>

          <button type="submit" className="btn btn-primary">
            Run audit
          </button>
        </form>
      )}

      {step === "running" && (
        <div className="card loading">
          <div className="spinner" />
          <div>{statusText}</div>
        </div>
      )}

      {step === "results" && appointmentId && (
        <>
          <AuditReportView appointmentId={appointmentId} />
          <div style={{ marginTop: 24 }}>
            <button className="btn" onClick={reset}>
              Run another audit
            </button>
          </div>
        </>
      )}

    </div>
  );
}
